<template>
    <Teleport v-if="requiresTwoFactor" defer to="#sticky-banner-content">
        <div class="flex items-center justify-between gap-4">
            <div class="flex items-center gap-2">
                <Warning class="size-4 shrink-0" />
                <span>{{ trans('profile.information.two_factor_authentication.requires_two_factor') }}</span>
            </div>
            <a v-if="!twoFactorEnabled && !twoFactorPending" class="shrink-0 font-semibold underline" href="#" @click.prevent="onBannerEnable">
                {{ trans('profile.information.two_factor_authentication.action.enable') }}
            </a>
        </div>
    </Teleport>
    <jet-form-section class="!mt-0" v-bind="$attrs">
        <template #title>
            {{ trans('profile.information.two_factor_authentication.title') }}
            <span v-if="requiresTwoFactor" class="text-danger">*</span>
        </template>
        <template #form>
            <div class="space-y-6">
                <section class="space-y-2">
                    <p class="text-sm text-muted">
                        {{ trans('profile.information.two_factor_authentication.description') }}
                    </p>
                    <p v-if="twoFactorEnabled" class="text-success text-sm">
                        <ShieldCheck class="-mt-1 inline-block size-4" />
                        {{ trans('profile.information.two_factor_authentication.enabled.status') }}
                    </p>
                    <div v-else class="text-sm">
                        <p v-if="twoFactorPending" class="text-primary">
                            <InfoIcon class="-mt-1 inline-block" />
                            {{ trans('profile.information.two_factor_authentication.pending.status') }}
                        </p>
                        <p v-else-if="requiresTwoFactor" class="text-danger">
                            <Warning class="inline-block size-4" /> {{ trans('profile.information.two_factor_authentication.requires_two_factor') }}
                        </p>
                        <p v-else class="text-muted">
                            {{ trans('profile.information.two_factor_authentication.disabled.content.header') }}
                        </p>
                    </div>
                    <p v-if="!twoFactorEnabled" class="text-sm leading-relaxed text-muted">
                        {{ trans('profile.information.two_factor_authentication.content.description') }}
                    </p>
                </section>

                <template v-if="twoFactorPending">
                    <div v-if="pendingDataNeedsConfirmation" class="border-t border-panel-border pt-4">
                        <p class="mb-3 text-sm text-muted">{{ trans('profile.information.two_factor_authentication.pending.confirm_password') }}</p>
                        <jet-confirms-password @confirmed="loadPendingSetupData">
                            <jet-button type="button" variant="secondary">{{
                                trans('profile.information.two_factor_authentication.pending.show_setup_button')
                            }}</jet-button>
                        </jet-confirms-password>
                    </div>

                    <div v-else class="grid gap-6 border-t border-panel-border pt-4 md:grid-cols-2">
                        <div class="space-y-3">
                            <p class="text-sm text-muted">
                                {{ trans('profile.information.two_factor_authentication.content.qr_code.description') }}
                            </p>
                            <div v-if="qrCode" class="text-center">
                                <div
                                    class="inline-block overflow-hidden rounded-lg border border-panel-border bg-white p-1 text-center"
                                    v-html="qrCode"
                                />
                            </div>
                        </div>
                        <div class="space-y-3">
                            <p class="text-sm font-semibold">
                                {{ trans('profile.information.two_factor_authentication.content.recovery_codes.description') }}
                            </p>
                            <div
                                :class="[
                                    'relative grid min-h-44 grid-cols-1 gap-0 rounded-lg border border-panel-border bg-panel p-4 text-center font-mono text-sm',
                                    recoveryCodes.length > 0 ? 'cursor-pointer' : 'place-content-center',
                                ]"
                                @click="copyRecoveryCodes"
                            >
                                <span v-for="code in recoveryCodes" :key="code">{{ code }}</span>
                                <LoaderCircle v-if="recoveryCodes.length === 0" class="mx-auto size-6 animate-spin text-muted" />
                                <span
                                    v-if="copied"
                                    class="text-success absolute inset-0 flex items-center justify-center rounded-lg bg-panel/90 text-xs font-semibold"
                                >
                                    {{ trans('profile.information.two_factor_authentication.content.recovery_codes.copied') }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <section v-if="!pendingDataNeedsConfirmation" class="space-y-4 rounded-xl border border-panel-border bg-blue-light p-6">
                        <h2 class="flex items-center gap-1 !text-xl font-semibold text-brand-blue">
                            <ShieldCheck class="size-6 text-primary" />
                            {{ trans('profile.information.two_factor_authentication.pending.verify.title') }}
                        </h2>
                        <p class="text-sm text-muted">
                            {{ trans('profile.information.two_factor_authentication.pending.verify.description') }}
                        </p>
                        <div class="flex flex-wrap items-end gap-2">
                            <div class="w-48">
                                <label class="mb-2 block text-xs font-semibold tracking-wide text-muted uppercase">{{
                                    trans('profile.information.two_factor_authentication.pending.verify.code_label')
                                }}</label>
                                <input
                                    ref="confirmationCodeInput"
                                    v-model="confirmationCode"
                                    :disabled="confirming"
                                    autocomplete="one-time-code"
                                    class="two-factor-otp-input w-full rounded-lg border border-panel-border px-4 py-2.5 text-center font-mono text-lg transition-colors outline-none disabled:cursor-not-allowed disabled:opacity-60"
                                    inputmode="numeric"
                                    maxlength="6"
                                    placeholder="000000"
                                    type="text"
                                    @keyup.enter="confirmTwoFactorAuthentication"
                                />
                            </div>
                            <jet-button
                                :disabled="confirmationCode.length < 6"
                                :is-processing="confirming"
                                type="button"
                                @click="confirmTwoFactorAuthentication"
                            >
                                {{ trans('profile.information.two_factor_authentication.pending.verify.submit') }}
                            </jet-button>
                        </div>
                        <p v-if="confirmationError" class="m-0 text-sm text-danger">
                            {{ confirmationError }}
                        </p>
                    </section>

                    <div v-if="canDisableTwoFactor" class="flex justify-end">
                        <jet-confirms-password @confirmed="disableTwoFactorAuthentication">
                            <jet-button :is-processing="disabling" type="button" variant="secondary">
                                {{ trans('profile.information.two_factor_authentication.pending.cancel') }}
                            </jet-button>
                        </jet-confirms-password>
                    </div>
                </template>

                <template v-else-if="twoFactorEnabled">
                    <div v-if="recoveryCodes.length > 0" class="space-y-3">
                        <p class="text-sm font-semibold">
                            {{ trans('profile.information.two_factor_authentication.content.recovery_codes.description') }}
                        </p>
                        <div
                            class="relative flex w-fit cursor-pointer flex-col gap-1 rounded-lg border border-panel-border bg-panel p-4 font-mono text-sm"
                            @click="copyRecoveryCodes"
                        >
                            <span v-for="code in recoveryCodes" :key="code">{{ code }}</span>
                            <span
                                v-if="copied"
                                class="text-success absolute inset-0 flex items-center justify-center rounded-lg bg-panel/90 text-xs font-semibold"
                            >
                                {{ trans('profile.information.two_factor_authentication.content.recovery_codes.copied') }}
                            </span>
                        </div>
                    </div>

                    <div class="flex flex-wrap gap-3 border-t border-panel-border pt-4">
                        <jet-confirms-password @confirmed="regenerateRecoveryCodes">
                            <jet-button :is-processing="regenerating" variant="primary">
                                {{ trans('profile.information.two_factor_authentication.action.regenerate_recovery_codes') }}
                            </jet-button>
                        </jet-confirms-password>

                        <jet-confirms-password @confirmed="disableTwoFactorAuthentication">
                            <jet-button :is-processing="disabling" variant="secondary">
                                {{
                                    canDisableTwoFactor
                                        ? trans('profile.information.two_factor_authentication.action.disable')
                                        : trans('profile.information.two_factor_authentication.action.reset')
                                }}
                            </jet-button>
                        </jet-confirms-password>
                    </div>
                </template>

                <template v-else>
                    <div class="flex justify-center border-t border-panel-border pt-4">
                        <jet-confirms-password ref="enableConfirmRef" @confirmed="enableTwoFactorAuthentication">
                            <jet-button :is-processing="enabling" type="button">
                                {{ trans('profile.information.two_factor_authentication.action.enable') }}
                            </jet-button>
                        </jet-confirms-password>
                    </div>
                </template>
            </div>
        </template>
    </jet-form-section>
</template>

<script setup>
defineOptions({ inheritAttrs: false });
import JetButton from '@/Jetstream/Button.vue';
import JetConfirmsPassword from '@/Jetstream/ConfirmsPassword.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import { $http } from '@/bootstrap.js';
import { eventBus, events } from '@/eventBus.js';
import { router, usePage } from '@inertiajs/vue3';
import { LoaderCircle, ShieldCheck } from '@lucide/vue';
import { trans } from '@/mixins/i18n.js';
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import InfoIcon from '@/Components/InfoIcon.vue';
import Warning from '../../../icons/Warning.vue';

const page = usePage();

const enabling = ref(false);
const disabling = ref(false);
const confirming = ref(false);
const regenerating = ref(false);
const copied = ref(false);
const qrCode = ref(null);
const recoveryCodes = ref([]);
const confirmationCode = ref('');
const confirmationError = ref(null);
const pendingDataNeedsConfirmation = ref(false);
const confirmationCodeInput = ref(null);
const enableConfirmRef = ref(null);

const twoFactorAuthenticationStatus = computed(() => (enabling.value ? 'disabled' : page.props.two_factor_authentication_status));
const twoFactorEnabled = computed(() => twoFactorAuthenticationStatus.value === 'enabled');
const twoFactorPending = computed(() => twoFactorAuthenticationStatus.value === 'pending');
const requiresTwoFactor = computed(() => page.props.requiresTwoFactor);
const canDisableTwoFactor = computed(() => page.props.canDisableTwoFactor);

watch(twoFactorPending, (isPending) => {
    if (isPending && !pendingDataNeedsConfirmation.value) {
        nextTick(() => confirmationCodeInput.value?.focus());
    }
});

watch(pendingDataNeedsConfirmation, (needsConfirmation) => {
    if (!needsConfirmation) {
        nextTick(() => confirmationCodeInput.value?.focus());
    }
});

const showQrCode = () => {
    return $http.get(route('two-factor.qr-code')).then((response) => {
        qrCode.value = response.data.svg;
    });
};

const showRecoveryCodes = () => {
    return $http.get(route('two-factor.recovery-codes')).then((response) => {
        recoveryCodes.value = response.data;
    });
};

const copyRecoveryCodes = () => {
    if (!navigator.clipboard || recoveryCodes.value.length === 0) {
        return;
    }

    navigator.clipboard
        .writeText(recoveryCodes.value.join('\n'))
        .then(() => {
            copied.value = true;
            setTimeout(() => (copied.value = false), 2000);
        })
        .catch(() => {
            // Clipboard API blocked by permissions policy — silently ignore
        });
};

const enableTwoFactorAuthentication = () => {
    enabling.value = true;
    router.post(
        route('two-factor.enable'),
        {},
        {
            preserveScroll: true,
            onSuccess: () => loadPendingSetupData(),
            onFinish: () => (enabling.value = false),
        },
    );
};

const confirmTwoFactorAuthentication = () => {
    confirming.value = true;
    confirmationError.value = null;

    $http
        .post(route('two-factor.confirm'), { code: confirmationCode.value })
        .then(() => {
            confirmationCode.value = '';
            recoveryCodes.value = [];
            router.reload({ only: ['auth', 'two_factor_authentication_status', 'requiresTwoFactor', 'canDisableTwoFactor'] });
        })
        .catch((error) => {
            const errors = error.response?.data?.errors ?? {};
            confirmationError.value =
                errors.code?.[0] ??
                Object.values(errors).flat()[0] ??
                trans('profile.information.two_factor_authentication.pending.verify.invalid_code');
        })
        .finally(() => {
            confirming.value = false;
        });
};

const loadPendingSetupData = () => {
    pendingDataNeedsConfirmation.value = false;
    return Promise.all([showQrCode(), showRecoveryCodes()]).catch((error) => {
        if (error.response?.status === 423) {
            qrCode.value = null;
            recoveryCodes.value = [];
            pendingDataNeedsConfirmation.value = true;
            return;
        }

        eventBus.$emit(events.presentFlashMessage, {
            payload: {
                message: trans('profile.information.two_factor_authentication.pending.load_error'),
                status: 'error',
            },
        });
    });
};

const regenerateRecoveryCodes = () => {
    regenerating.value = true;
    $http
        .post(route('two-factor.regenerate-recovery-codes'))
        .then(() => {
            recoveryCodes.value = [];
            return showRecoveryCodes();
        })
        .catch(() => {
            eventBus.$emit(events.presentFlashMessage, {
                payload: {
                    message: trans('profile.information.two_factor_authentication.pending.load_error'),
                    status: 'error',
                },
            });
        })
        .finally(() => {
            regenerating.value = false;
        });
};

const disableTwoFactorAuthentication = () => {
    disabling.value = true;
    router.delete(route('two-factor.disable'), {
        preserveScroll: true,
        onSuccess: () => {
            qrCode.value = null;
            recoveryCodes.value = [];
        },
        onFinish: () => (disabling.value = false),
    });
};

if (twoFactorPending.value) {
    loadPendingSetupData().then(() => nextTick(() => confirmationCodeInput.value?.focus()));
}

function onBannerEnable() {
    document.getElementById('two-factor-authentication')?.scrollIntoView({ behavior: 'smooth' });
    enableConfirmRef.value?.startConfirmingPassword();
}

watch(
    requiresTwoFactor,
    (current, previous) => {
        if (current === previous) {
            return;
        }
        eventBus.$emit(current ? events.showStickyBanner : events.hideStickyBanner);
    },
    { immediate: true },
);

onBeforeUnmount(() => {
    eventBus.$emit(events.hideStickyBanner);
});
</script>

<style scoped>
.two-factor-otp-input {
    letter-spacing: 0.4em;
}

.two-factor-otp-input:focus {
    border-color: var(--color-primary) !important;
}
</style>
